import React, { useState } from 'react';
import { Upload, Download, AlertCircle, FileJson, X, CheckCircle } from 'lucide-react';

const RelationshipImportExport = ({ 
  currentData, 
  table, 
  availableTables,
  onImport,
  onClose 
}) => {
  const [importing, setImporting] = useState(false);
  const [importData, setImportData] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [showPreview, setShowPreview] = useState(false);

  // Export current relationships as JSON
  const handleExport = () => {
    // Check if there are any relationships defined
    const hasRelationships = currentData.primaryKey || 
                           (currentData.foreignKeys && currentData.foreignKeys.length > 0);

    let exportData;
    
    if (hasRelationships) {
      // Export actual relationships
      exportData = {
        primaryKey: currentData.primaryKey,
        foreignKeys: currentData.foreignKeys
      };
    } else {
      // Export template with examples
      exportData = {
        primaryKey: "id_column_name_or_null",
        "primaryKey_composite_example": ["column1", "column2"],
        foreignKeys: [
          {
            "columns": ["customer_id"],
            "references": {
              "table": "customers",
              "columns": ["id"]
            },
            "type": "n:1"
          },
          {
            "columns": ["product_id", "variant_id"],
            "references": {
              "table": "product_variants",
              "columns": ["product_id", "variant_id"]
            },
            "type": "n:1",
            "_comment": "Example of composite foreign key"
          }
        ],
        "_instructions": {
          "primaryKey": "Set to column name (string), array of columns (composite), or null",
          "foreignKeys": "Array of foreign key definitions. Can be empty []",
          "type_options": ["n:1", "1:1", "1:n", "n:n"],
          "note": "Remove all fields starting with underscore and the example fields before importing"
        }
      };
    }

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${table.name}_relationships.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Validate imported data
  const validateImportData = (data) => {
    const errors = [];

    // Remove any fields starting with underscore (comments/instructions)
    const cleanData = Object.keys(data).reduce((acc, key) => {
      if (!key.startsWith('_')) {
        acc[key] = data[key];
      }
      return acc;
    }, {});

    // Check if basic structure exists
    if (!('primaryKey' in cleanData) || !('foreignKeys' in cleanData)) {
      errors.push("JSON must contain 'primaryKey' and 'foreignKeys' fields.");
      return errors;
    }

    const { primaryKey, foreignKeys } = cleanData;

    // Validate primary key
    if (primaryKey !== null) {
      if (Array.isArray(primaryKey)) {
        // Composite primary key
        if (primaryKey.length === 0) {
          errors.push("Composite primary key array cannot be empty. Use null for no primary key.");
        }
        primaryKey.forEach(col => {
          if (typeof col !== 'string') {
            errors.push(`Primary key column must be a string, got ${typeof col}.`);
          } else if (!table.schema?.some(field => field.name === col)) {
            errors.push(`Primary key column "${col}" does not exist in table.`);
          }
        });
        // Check for duplicates
        if (new Set(primaryKey).size !== primaryKey.length) {
          errors.push("Duplicate columns in composite primary key.");
        }
      } else if (typeof primaryKey === 'string') {
        // Single primary key
        if (!table.schema?.some(field => field.name === primaryKey)) {
          errors.push(`Primary key column "${primaryKey}" does not exist in table.`);
        }
      } else if (primaryKey !== null) {
        errors.push("Primary key must be a string, array of strings, or null.");
      }
    }

    // Validate foreign keys
    if (!Array.isArray(foreignKeys)) {
      errors.push("foreignKeys must be an array (can be empty []).");
      return errors;
    }

    foreignKeys.forEach((fk, index) => {
      // Skip if it's an example entry
      if (fk._comment || fk._example) return;

      // Check structure
      if (!fk.columns || !fk.references || !fk.type) {
        errors.push(`Foreign key ${index + 1}: Must have 'columns', 'references', and 'type' fields.`);
        return;
      }

      if (!Array.isArray(fk.columns)) {
        errors.push(`Foreign key ${index + 1}: 'columns' must be an array.`);
        return;
      }

      if (!fk.references.table || !Array.isArray(fk.references.columns)) {
        errors.push(`Foreign key ${index + 1}: 'references' must have 'table' (string) and 'columns' (array).`);
        return;
      }

      // Validate columns exist in current table
      fk.columns.forEach(col => {
        if (!table.schema?.some(field => field.name === col)) {
          errors.push(`Foreign key column "${col}" does not exist in table.`);
        }
      });

      // Validate referenced table exists
      if (!availableTables.some(t => t.name === fk.references.table)) {
        errors.push(`Referenced table "${fk.references.table}" not found or not accessible.`);
      } else {
        // Validate referenced columns exist
        const refTable = availableTables.find(t => t.name === fk.references.table);
        fk.references.columns.forEach((col, i) => {
          if (!refTable.schema?.some(field => field.name === col)) {
            errors.push(`Referenced column "${col}" does not exist in table "${fk.references.table}".`);
          }
        });
      }

      // Validate column counts match
      if (fk.columns.length !== fk.references.columns.length) {
        errors.push(`Foreign key ${index + 1}: Number of columns (${fk.columns.length}) must match referenced columns (${fk.references.columns.length}).`);
      }

      // Validate relationship type
      const validTypes = ['n:1', '1:1', '1:n', 'n:n'];
      if (!validTypes.includes(fk.type)) {
        errors.push(`Foreign key ${index + 1}: Invalid type "${fk.type}". Must be one of: ${validTypes.join(', ')}`);
      }
    });

    // Check for duplicate foreign key columns
    const allFkColumns = foreignKeys
      .filter(fk => !fk._comment && !fk._example)
      .flatMap(fk => fk.columns);
    if (new Set(allFkColumns).size !== allFkColumns.length) {
      errors.push("Each column can only have one foreign key reference.");
    }

    return errors;
  };

  // Handle file upload
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target.result);
        const errors = validateImportData(data);
        
        if (errors.length > 0) {
          setValidationErrors(errors);
          setImportData(null);
          setShowPreview(false);
        } else {
          // Clean the data before preview
          const cleanData = {
            primaryKey: data.primaryKey,
            foreignKeys: data.foreignKeys.filter(fk => !fk._comment && !fk._example)
          };
          setImportData(cleanData);
          setValidationErrors([]);
          setShowPreview(true);
        }
      } catch (error) {
        setValidationErrors(['Invalid JSON file. Please check the file format.']);
        setImportData(null);
        setShowPreview(false);
      }
    };
    reader.readAsText(file);
    
    // Reset file input
    event.target.value = '';
  };

  // Apply imported data
  const handleApplyImport = () => {
    if (!importData) return;

    onImport(importData);
    setImporting(false);
    setImportData(null);
    setShowPreview(false);
    onClose();
  };

  // Format display for preview
  const formatRelationshipDisplay = (rel) => {
    if (Array.isArray(rel)) {
      return `[${rel.join(', ')}]`;
    }
    return rel || 'None';
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between pb-3 border-b">
        <h3 className="text-lg font-medium text-gray-900 flex items-center">
          <FileJson className="h-5 w-5 mr-2" />
          Import/Export Relationships
        </h3>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-500"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-sm text-blue-700">
        <p className="font-medium mb-1">Simple JSON Format</p>
        <p className="text-blue-600">
          Export to see the expected format. If no relationships exist, you'll get a template with examples.
        </p>
      </div>

      {/* Format Example */}
      <div className="bg-gray-50 rounded-md p-3">
        <p className="text-xs font-mono text-gray-600">
          {`{
  "primaryKey": "column_name",
  "foreignKeys": [{
    "columns": ["fk_column"],
    "references": {
      "table": "ref_table",
      "columns": ["ref_column"]
    },
    "type": "n:1"
  }]
}`}
        </p>
      </div>

      {/* Action Buttons */}
      <div className="flex space-x-3">
        <button
          onClick={handleExport}
          className="flex-1 flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
        >
          <Download className="h-4 w-4 mr-2" />
          Export Current
        </button>
        
        <label className="flex-1 flex items-center justify-center px-4 py-2 border border-primary rounded-md text-sm font-medium text-primary bg-white hover:bg-primary/5 cursor-pointer">
          <Upload className="h-4 w-4 mr-2" />
          Import JSON
          <input
            type="file"
            accept=".json"
            onChange={handleFileUpload}
            className="hidden"
          />
        </label>
      </div>

      {/* Validation Errors */}
      {validationErrors.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex items-start">
            <AlertCircle className="h-5 w-5 text-red-400 mt-0.5 mr-2 flex-shrink-0" />
            <div>
              <h4 className="text-sm font-medium text-red-800 mb-1">Validation Failed</h4>
              <ul className="text-sm text-red-700 space-y-1">
                {validationErrors.map((error, index) => (
                  <li key={index}>• {error}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Preview */}
      {showPreview && importData && (
        <div className="border border-gray-200 rounded-md p-4 space-y-3">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-gray-900">Import Preview</h4>
            <CheckCircle className="h-5 w-5 text-green-500" />
          </div>

          {/* Primary Key Preview */}
          <div>
            <span className="text-xs font-medium text-gray-500 uppercase">Primary Key</span>
            <div className="mt-1 text-sm font-mono bg-gray-50 px-3 py-2 rounded">
              {formatRelationshipDisplay(importData.primaryKey)}
            </div>
          </div>

          {/* Foreign Keys Preview */}
          <div>
            <span className="text-xs font-medium text-gray-500 uppercase">
              Foreign Keys ({importData.foreignKeys?.length || 0})
            </span>
            {importData.foreignKeys && importData.foreignKeys.length > 0 ? (
              <div className="mt-1 space-y-2">
                {importData.foreignKeys.map((fk, index) => (
                  <div key={index} className="text-sm bg-gray-50 px-3 py-2 rounded">
                    <div className="font-mono">
                      {formatRelationshipDisplay(fk.columns)} → {fk.references.table}.{formatRelationshipDisplay(fk.references.columns)}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Type: {fk.type}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="mt-1 text-sm text-gray-500 italic">No foreign keys defined</div>
            )}
          </div>

          {/* Apply Button */}
          <div className="flex justify-end space-x-3 pt-3 border-t">
            <button
              onClick={() => {
                setShowPreview(false);
                setImportData(null);
              }}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900"
            >
              Cancel
            </button>
            <button
              onClick={handleApplyImport}
              className="px-4 py-2 text-sm text-white bg-primary rounded-md hover:bg-primary/90"
            >
              Apply Import
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RelationshipImportExport;